import { buttonStyle } from 'components/buttons/buttonStyle';
import CircularLoader from 'components/CircularLoader';
import { DatePickerField } from 'components/forms/inputs/common/DatePickerField';
import { SearchOutlined } from 'components/icons';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { addDays, format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useMemo, useState } from 'react';
import { cn } from 'utils/cn';
import { DEFAULT_DATE_FORMAT_PATTERN } from 'utils/dateFormat';
import { t } from 'utils/getTranslations';
import {
    addPeriod,
    canRemovePeriod,
    groupPeopleByRole,
    PeriodDateError,
    PeriodDraft,
    PRICE_PATTERN,
    PricedPerson,
    redatePeriod,
    setPrice,
    sortPeriods,
} from './employeePricing';
import { undatedPeriodLabel } from './matterPricelistEditorUtils';

interface Props {
    periods: PeriodDraft[];
    activeKey: string;
    onActiveKeyChange: (key: string) => void;
    onPeriodsChange: (periods: PeriodDraft[]) => void;
    people: Map<number, PricedPerson>;
    roleOrder?: Map<number, number>;
    dateErrors: Record<string, PeriodDateError>;
    isValidating: boolean;
    today: string;
    isLoading?: boolean;
}

const DATE_ERROR_MESSAGES: Record<PeriodDateError, string> = {
    required: 'pricelists.employee.date-required',
    duplicate: 'pricelists.employee.date-duplicate',
    'out-of-order': 'pricelists.employee.date-out-of-order',
};

function toDate(value: string | null): Date | null {
    return value ? parseISO(value) : null;
}

// Today, or the day after the latest period when that one starts later, so a new period lands last and free.
function newPeriodDate(periods: PeriodDraft[], today: string): string {
    const latest = sortPeriods(periods).at(-1)?.validFrom;
    if (!latest || latest < today) return today;
    return format(addDays(parseISO(latest), 1), DEFAULT_DATE_FORMAT_PATTERN);
}

/**
 * The per-colleague prices of an employee-priced list: its periods as tabs, the open period's start date, and
 * every colleague grouped by the job role they hold today. A price may stay empty; empty bills the same as no row.
 */
export function EmployeeRatesEditForm({
    periods,
    activeKey,
    onActiveKeyChange,
    onPeriodsChange,
    people,
    roleOrder,
    dateErrors,
    isValidating,
    today,
    isLoading,
}: Props): JSX.Element {
    const dateFormat = useEffectiveDateFormat();
    const [search, setSearch] = useState('');
    const activePeriod = periods.find((period) => period.key === activeKey) ?? periods[0];
    const groups = useMemo(
        () => groupPeopleByRole(people, activePeriod, search, roleOrder),
        [people, activePeriod, search, roleOrder]
    );
    const formatDay = (value: string) => format(parseISO(value), dateFormat);

    const tabLabel = (period: PeriodDraft): string =>
        period.validFrom === null
            ? undatedPeriodLabel(periods, formatDay)
            : t('pricelists.employee.period-from', { date: formatDay(period.validFrom) });

    const updateActive = (next: PeriodDraft) => {
        const replaced = periods.map((period) => (period.key === next.key ? next : period));
        // A period added in this edit takes its place by date; a stored one keeps its place, so its date cannot
        // pass a neighbour unnoticed.
        onPeriodsChange(next.id === null && next.validFrom !== null ? sortPeriods(replaced) : replaced);
    };

    const onAdd = () => {
        const added = addPeriod(periods, newPeriodDate(periods, today));
        onPeriodsChange(added.periods);
        onActiveKeyChange(added.key);
    };

    const onRemove = (key: string) => {
        const index = periods.findIndex((period) => period.key === key);
        const remaining = periods.filter((period) => period.key !== key);
        onPeriodsChange(remaining);
        if (activeKey === key) {
            onActiveKeyChange(remaining[Math.max(0, index - 1)].key);
        }
    };

    const onPriceChange = (person: PricedPerson, value: string) => {
        if (PRICE_PATTERN.test(value)) {
            updateActive(setPrice(activePeriod, person.id, value));
        }
    };

    const dateError = isValidating ? dateErrors[activePeriod.key] : undefined;
    const activeIndex = periods.indexOf(activePeriod);

    return (
        <div className={cn('relative flex w-full flex-col', isLoading && 'opacity-50')}>
            {isLoading ? <CircularLoader /> : null}
            <div className="flex items-center justify-between gap-3 pb-1.5">
                <span className="text-[0.85rem] font-semibold">{t('pricelists.hourly-rates')}</span>
            </div>

            <div className="flex min-w-0 items-center gap-3 border-b border-zinc-300/60">
                <LineTabs
                    value={activePeriod.key}
                    onValueChange={(key) => onActiveKeyChange(String(key))}
                    className="min-w-0"
                >
                    <LineTabsList arrows={{ back: t('back'), next: t('next') }} className="border-b-0">
                        {periods.map((period) => (
                            <LineTab key={period.key} value={period.key} size="compact" reserveSelectedWidth={false}>
                                <span className="flex items-center gap-1.5">
                                    {tabLabel(period)}
                                    {canRemovePeriod(period, today) ? (
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            aria-label={t('pricelists.employee.remove-period')}
                                            className="text-grey-500 cursor-pointer rounded-full px-1 leading-none hover:bg-zinc-200"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onRemove(period.key);
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    onRemove(period.key);
                                                }
                                            }}
                                        >
                                            ✕
                                        </span>
                                    ) : null}
                                </span>
                            </LineTab>
                        ))}
                    </LineTabsList>
                </LineTabs>
                <Button {...buttonStyle({ variant: 'text', size: 'extraSmall' }, 'flex-none')} onClick={onAdd}>
                    {t('pricelists.employee.add-period')}
                </Button>
            </div>

            <div className="flex flex-col pt-2 pb-1">
                <div className="flex items-center gap-2 text-[0.8rem]">
                    <span className="text-grey-700 flex-1 font-semibold">{t('pricelists.employee.applies-from')}</span>
                    {activePeriod.validFrom === null && activeIndex === 0 ? (
                        <span className="text-grey-500 text-[0.72rem]">
                            {periods.length > 1
                                ? t('pricelists.employee.first-period-covers')
                                : t('pricelists.employee.period-all-dates')}
                        </span>
                    ) : (
                        <DatePickerField
                            format={dateFormat}
                            value={toDate(activePeriod.validFrom)}
                            onChange={(date) =>
                                updateActive(
                                    redatePeriod(
                                        periods,
                                        activePeriod,
                                        date && !isNaN(date.getTime())
                                            ? format(date, DEFAULT_DATE_FORMAT_PATTERN)
                                            : null
                                    )
                                )
                            }
                            invalid={dateError ? true : undefined}
                            className="w-48"
                        />
                    )}
                </div>
                {dateError ? (
                    <div className="self-end pt-0.5 text-[0.65rem] text-red-600">
                        {t(DATE_ERROR_MESSAGES[dateError])}
                    </div>
                ) : null}
            </div>

            <InputGroup className="bg-paper my-1.5">
                <InputGroupAddon>
                    <SearchOutlined className="text-grey-500 size-3.5" />
                </InputGroupAddon>
                <InputGroupInput
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t('search')}
                    aria-label={t('pricelists.employee.search-colleagues')}
                    className="text-xs md:text-xs"
                />
            </InputGroup>

            {groups.length === 0 ? (
                <div className="text-grey-500 py-3 text-[0.78rem]">{t('pricelists.employee.no-colleagues')}</div>
            ) : null}
            {groups.map((group) => (
                <div key={group.key} role="group" aria-label={group.jobRoleName ?? t('pricelists.employee.no-role')}>
                    <div className="text-grey-600 pt-2.5 pb-1 text-[0.7rem] font-semibold tracking-wider uppercase">
                        {group.jobRoleName ?? t('pricelists.employee.no-role')}
                    </div>
                    {group.people.map((person) => (
                        <div key={person.id} className="flex items-center gap-2 py-0.5">
                            <span className="text-grey-700 flex min-w-0 flex-1 items-center gap-1.5 text-[0.8rem]">
                                <span className="truncate">{person.name}</span>
                                {!person.isActive ? (
                                    <span className="text-grey-500 shrink-0 text-[0.7rem]">
                                        {person.leftOn
                                            ? t('pricelists.employee.left-on', { date: formatDay(person.leftOn) })
                                            : t('pricelists.employee.no-longer-active')}
                                    </span>
                                ) : null}
                            </span>
                            <Input
                                aria-label={t('pricelists.employee.price-of', { name: person.name })}
                                className="bg-paper w-20 flex-none text-right text-xs tabular-nums md:text-xs"
                                placeholder={t('pricelists.employee.no-price')}
                                value={activePeriod.prices[person.id] ?? ''}
                                onChange={(event) => onPriceChange(person, event.target.value)}
                            />
                            {!person.isActive ? (
                                <button
                                    type="button"
                                    aria-label={t('pricelists.employee.remove-person', { name: person.name })}
                                    className="text-grey-500 size-6 flex-none cursor-pointer rounded-full leading-none hover:bg-zinc-200"
                                    onClick={() => updateActive(setPrice(activePeriod, person.id, ''))}
                                >
                                    ✕
                                </button>
                            ) : (
                                <span className="size-6 flex-none" aria-hidden />
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
